'use client';

import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import {
  libraryItemToStoredMediaFile,
  MediaPicker,
  type MediaFile,
  type MediaPickerLibraryItem,
  type PickerMediaType,
  type UploadResult,
} from '@kit/uploads';

/**
 * Library kinds a file field accepts, derived from its MIME allowlist. An empty
 * or unrecognised allowlist means "anything", which the picker reads as no filter.
 */
function pickerTypesFor(allowedTypes: string[] | undefined): PickerMediaType[] {
  const types = new Set<PickerMediaType>();
  for (const mime of allowedTypes ?? []) {
    if (mime.startsWith('image/')) {
      types.add('photo');
      types.add('headshot');
    } else if (mime.startsWith('video/')) {
      types.add('video');
    } else if (mime === 'application/pdf') {
      types.add('document');
      types.add('pdf');
    }
  }
  return [...types];
}

const subscribeNever = () => () => {};

/** False while the server-rendered HTML is hydrating, true on every client render after. */
function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );
}

function uploadBucketFor(
  types: PickerMediaType[],
): 'photo' | 'video' | 'document' {
  if (types.includes('video')) return 'video';
  if (types.includes('document')) return 'document';
  return 'photo';
}

interface FileLibraryPickerProps {
  /** Files already in the field's answer. */
  value: MediaFile[];
  multiple: boolean;
  allowedTypes?: string[];
  maxFiles?: number;
  loadLibrary: () => Promise<MediaPickerLibraryItem[]>;
  uploadFile: (formData: FormData) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  /** Receives the field's next answer: one file, a list, or null when emptied. */
  onChange: (next: MediaFile | MediaFile[] | null) => void;
}

/**
 * "From my library" control for a file field. Lets the applicant attach items
 * that already sit in their media library, private ones included, and stores
 * each pick in the same `MediaFile` shape an upload produces. The app supplies
 * `loadLibrary`; this package never learns where the library lives.
 */
export function FileLibraryPicker({
  value,
  multiple,
  allowedTypes,
  maxFiles,
  loadLibrary,
  uploadFile,
  onChange,
}: FileLibraryPickerProps) {
  const { t } = useTranslation();
  const hydrated = useHydrated();
  const types = pickerTypesFor(allowedTypes);

  // Rows the picker has shown, by id, so a confirmed id resolves to a full file.
  const known = useRef(new Map<string, MediaPickerLibraryItem>());
  const remember = useCallback((rows: MediaPickerLibraryItem[]) => {
    for (const row of rows) known.current.set(row.id, row);
  }, []);

  const load = useCallback(async () => {
    const rows = await loadLibrary();
    remember(rows);
    return rows;
  }, [loadLibrary, remember]);

  const handleChange = (picked: string | string[] | null) => {
    const ids = picked == null ? [] : Array.isArray(picked) ? picked : [picked];
    const next = ids.flatMap((id): MediaFile[] => {
      const current = value.find((file) => file.id === id);
      if (current) return [current];
      const row = known.current.get(id);
      return row ? [libraryItemToStoredMediaFile(row)] : [];
    });
    if (multiple) onChange(next);
    else onChange(next[0] ?? null);
  };

  // The button is client-only: a click on server-rendered markup that has not
  // hydrated yet would be dropped without opening the dialog.
  if (!hydrated) return null;

  // No `deleteAction`: deleting a library item is the library page's job, and
  // from an application form it would only look like a detach.
  return (
    <MediaPicker
      types={types}
      mode={multiple ? 'multiple' : 'single'}
      maxSelected={multiple ? maxFiles : undefined}
      value={multiple ? value.map((file) => file.id) : (value[0]?.id ?? null)}
      onChange={handleChange}
      mediaTypeForUpload={uploadBucketFor(types)}
      allowedTypesForUpload={allowedTypes}
      uploadAction={uploadFile}
      loadLibrary={load}
      onLibraryItemAdded={(item) => remember([item])}
      selectionDisplay="none"
      translations={{
        triggerLabel: t('auditions:formRenderer.library.open'),
        dialogTitle: t('auditions:formRenderer.library.title'),
        dialogDescription: t('auditions:formRenderer.library.description'),
        libraryTab: t('auditions:formRenderer.library.libraryTab'),
        uploadTab: t('auditions:formRenderer.library.uploadTab'),
        emptyLibrary: t('auditions:formRenderer.library.empty'),
        noTypeMatch: t('auditions:formRenderer.library.noMatch'),
        loading: t('auditions:formRenderer.library.loading'),
        cancel: t('auditions:formRenderer.library.cancel'),
        confirm: t('auditions:formRenderer.library.confirm'),
        privateLabel: t('auditions:formRenderer.library.private'),
      }}
    />
  );
}
